import { GetStateStorage } from 'common/storage';
import React, { useState, useRef } from 'react';
import { MailProDialog } from 'views/Pma/MailPro/components/MailProDialog/MailProDialog';
import { MailProDialogHeader } from 'views/Pma/MailPro/components/MailProDialog/MailProDialogHeader';
import { MailProDialogBody } from 'views/Pma/MailPro/components/MailProDialog/MailProDialogBody';
import Constants from 'common/constants';
import {
  LOG_MODULES,
  logFreshdeskSubmitFailed,
  logFreshdeskTicketRejected,
  logFreshdeskValidationFailed,
} from 'common/errorTracker';
import {
  appendFreshdeskTicketDefaults,
  getTeamMemberEmails,
  submitFreshdeskTicket,
} from 'common/freshdesk';

const FreshdeskAPIWidget = ({ children, currentArea = {} }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        file: null
    });
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const openDialog = () => {
        const stateData = GetStateStorage();
        const agentData = ((stateData || {}).userData) || {};
        const countyID = agentData.countyID || null;
        const countyName = countyID ? (Constants.COUNTY_ID_NAME_MAP[String(countyID)] || `County ID: ${countyID}`) : null;
        
        const subjectParts = [
            `Custom Cover Photo Request`,
            currentArea.mlsNeighborhood,
            currentArea.mlsCityName,
            countyName,
        ].filter(Boolean);

        setFormData({
            subject: subjectParts.join(' | '),
            description: "Please upload a custom cover photo for the PMA.",
            file: null
        });
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        resetForm();
    };

    const resetForm = () => {
        setFormData({
            subject: '',
            description: '',
            file: null
        });
        setMessage({ type: '', text: '' });
        setIsDragOver(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const MAX_FILE_SIZE_MB = 20;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const validateFile = (file, source) => {
        if (!file.type.startsWith('image/')) {
            logFreshdeskValidationFailed({
                module: LOG_MODULES.COVER_PHOTO,
                reason: `invalid_file_type_${source}`,
                currentArea,
            });
            setMessage({ type: 'error', text: 'Please select an image file only.' });
            return false;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            logFreshdeskValidationFailed({
                module: LOG_MODULES.COVER_PHOTO,
                reason: `file_too_large_${source}`,
                currentArea,
            });
            setMessage({ type: 'error', text: `File is too large. Please upload an image under 20MB.` });
            return false;
        }
        return true;
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && validateFile(file, 'select')) {
            setFormData(prev => ({ ...prev, file }));
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (validateFile(file, 'drop')) {
                setFormData(prev => ({ ...prev, file }));
            }
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const submitTicket = async () => {
        setIsSubmitting(true);
        const stateData = GetStateStorage();
        const agentData = ((stateData || {}).userData) || {};

        try {
            
            // Create FormData for file upload (required for attachments in v2 API)
            const formDataToSend = new FormData();
            
            // Get team member emails for CC
            const teamMemberEmails = getTeamMemberEmails(agentData);
            
            // Basic ticket information (following v2 API specification)
            formDataToSend.append('name', agentData.listgenUserFirstName + " " + agentData.listgenUserLastName);
            formDataToSend.append('email', agentData.emailAddress);
            formDataToSend.append('subject', formData.subject);
            formDataToSend.append('description', formData.description);
            
            // Add CC emails for all team members
            teamMemberEmails.forEach(email => {
                formDataToSend.append('cc_emails[]', email);
            });
            
            appendFreshdeskTicketDefaults(formDataToSend);
            
            // Add attachment if file is selected — sanitize filename to avoid
            // Freshdesk MIME detection failures on filenames with multiple dots
            if (formData.file) {
                const originalName = formData.file.name;
                const ext = originalName.split('.').pop();
                const baseName = originalName.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
                const safeName = `${baseName}.${ext}`;
                const safeFile = originalName !== safeName
                    ? new File([formData.file], safeName, { type: formData.file.type })
                    : formData.file;
                formDataToSend.append('attachments[]', safeFile);
            }

            await submitFreshdeskTicket(formDataToSend);

            const ccCount = teamMemberEmails.length;
            const teamNotificationText = ccCount > 0 ? ` Your team (${ccCount} members) has been notified.` : '';
            setMessage({ 
                type: 'success', 
                text: `Thank you! Your custom cover photo will be updated shortly.${teamNotificationText}` 
            });
            // Reset form after successful submission (but keep success message visible)
            setTimeout(() => {
                setFormData({
                    subject: '',
                    description: '',
                    file: null
                });
                setIsDragOver(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }, 2000);
        } catch (error) {
            if (error.response) {
                logFreshdeskTicketRejected({
                    module: LOG_MODULES.COVER_PHOTO,
                    response: error.response,
                    currentArea,
                    hasAttachment: Boolean(formData.file),
                    agentData,
                });
                setMessage({
                    type: 'error',
                    text: 'Failed to upload cover photo. Please try again.',
                });
                return;
            }
            // Network/CORS errors (TypeError with no response) often mean the request
            // reached Freshdesk and the ticket was created, but the browser blocked the
            // response (common on Windows Chrome with cross-origin requests). Treat as
            // likely success rather than failure.
            const isNetworkError = error instanceof TypeError || !error.response;
            logFreshdeskSubmitFailed({
                module: LOG_MODULES.COVER_PHOTO,
                error,
                currentArea,
                hasAttachment: Boolean(formData.file),
                agentData,
                httpStatus: isNetworkError ? 'NETWORK_ERROR' : undefined,
            });
            if (isNetworkError) {
                setMessage({
                    type: 'success',
                    text: 'Your request has been submitted. Our team will update your cover photo shortly.',
                });
            } else {
                setMessage({
                    type: 'error',
                    text: 'Failed to upload cover photo. Please try again.',
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <style>
                {`
                    @keyframes successPulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.02); }
                        100% { transform: scale(1); }
                    }
                `}
            </style>
            <div onClick={openDialog}>
                {children || <button type="button">Upload Custom Cover Photo</button>}
            </div>

            <MailProDialog
                open={isDialogOpen}
                onClose={closeDialog}
                aria-labelledby="freshdesk-api-dialog-title"
            >
                <MailProDialogHeader>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 16px' }}>
                        <h2 id="freshdesk-api-dialog-title" style={{
                            fontFamily: 'objektiv-mk2, sans-serif',
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: '500',
                            textAlign: 'center',
                            margin: '0 0 6px 0'
                        }}>
                            Custom Cover Photo Upload
                        </h2>
                        <p style={{
                            fontFamily: 'objektiv-mk2, sans-serif',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '400',
                            textAlign: 'center',
                            margin: 0,
                            opacity: 0.9
                        }}>
                            By uploading a custom image, you confirm that<br/>you own or have permission to use it for printed materials.
                        </p>
                    </div>
                </MailProDialogHeader>
                <MailProDialogBody>
                    <div style={{ padding: '20px', fontFamily: 'objektiv-mk2, sans-serif', position: 'relative' }}>
                        {/* Success/Error Messages */}
                        {message.type && (
                            <div style={{
                                backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                                border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                                color: message.type === 'success' ? '#155724' : '#721c24',
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '8px',
                                padding: '16px',
                                marginBottom: '20px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                animation: message.type === 'success' ? 'successPulse 0.6s ease-in-out' : 'none'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    <div style={{
                                        backgroundColor: message.type === 'success' ? '#28a745' : '#dc3545',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        flexShrink: 0,
                                        minWidth: '24px',
                                        minHeight: '24px'
                                    }}>
                                        {message.type === 'success' ? '✓' : '✗'}
                                    </div>
                                    <span style={{ 
                                        fontWeight: '500', 
                                        fontFamily: 'objektiv-mk2, sans-serif',
                                        fontSize: message.type === 'success' ? '16px' : '14px'
                                    }}>
                                        {message.text}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontFamily: 'objektiv-mk2, sans-serif' }}>
                                Description:
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    fontFamily: 'objektiv-mk2, sans-serif'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontFamily: 'objektiv-mk2, sans-serif' }}>
                                Upload a Cover Photo:
                            </label>
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                style={{
                                    width: '95%',
                                    padding: '20px',
                                    border: `2px dashed ${isDragOver ? '#4e85af' : '#666'}`,
                                    borderRadius: '8px',
                                    backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontFamily: 'objektiv-mk2, sans-serif',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '100px',
                                    textAlign: 'center'
                                }}
                            >
                                <div style={{ color: '#666', fontSize: '14px', marginBottom: '4px' }}>
                                    📁 Drag & drop Cover Photo here or click to browse
                                </div>
                                <div style={{ color: '#999', fontSize: '12px' }}>
                                    Only image files are allowed (max 20MB)
                                </div>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            {formData.file && (
                                <p style={{ marginTop: '8px', fontSize: '12px', color: '#666', fontFamily: 'objektiv-mk2, sans-serif' }}>
                                    Selected: {formData.file.name}
                                </p>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <button
                                onClick={closeDialog}
                                style={{
                                    padding: '10px 20px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    fontFamily: 'objektiv-mk2, sans-serif'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitTicket}
                                disabled={isSubmitting || !formData.file}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: '#4e85af',
                                    color: 'white',
                                    cursor: (isSubmitting || !formData.file) ? 'not-allowed' : 'pointer',
                                    opacity: (isSubmitting || !formData.file) ? 0.6 : 1,
                                    fontFamily: 'objektiv-mk2, sans-serif',
                                    marginLeft: '-32px'
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                            <div></div>
                            
                        </div>
                    </div>
                </MailProDialogBody>
            </MailProDialog>
        </>
    );
};

export default React.memo(FreshdeskAPIWidget);
